import React, { useEffect, useMemo, useRef, useState } from "react";
import styles from "./HorseNotificationsTab.module.css";
import i18n from "../../Services/i18n";
import NotificationService from "../../Services/notificationService";
import { useToasts } from "../../LayoutComponents/ToastContext";
import { useModal } from "../../Context/ModalContext";
import LoadingSpinner from "../../LayoutComponents/LoadingSpinner";
import {
  MdNotificationsActive,
  MdOutlineAccessTime,
  MdDeleteOutline,
  MdOutlineNotificationsPaused,
  MdNotificationImportant,
} from "react-icons/md";
import NotificationQuickForm from "../QuickFormComponents/NotificationQuickForm/NotificationQuickForm";

const HorseNotificationsTab = ({ horse }) => {
  const [notifications, setNotifications] = useState([]);
  const [activeNotificationIds, setActiveNotificationIds] = useState(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const notificationFormRef = useRef(null);

  const { openModal } = useModal();
  const { addToast } = useToasts();

  const loadNotifications = async () => {
    try {
      setIsLoading(true);

      const [notificationsResult, triggeredResult] = await Promise.all([
        NotificationService.GetNotifications(),
        NotificationService.GetTriggeredNotifications(),
      ]);

      setNotifications(notificationsResult ?? []);

      const activeIds = new Set(
        (triggeredResult ?? []).map((notification) => notification.externalId),
      );

      setActiveNotificationIds(activeIds);
    } catch (ex) {
      addToast(i18n.t("Generic.ServerError"), "BACKENDERROR", "ERROR", true);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!horse?.externalId) return;

    loadNotifications();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [horse?.externalId]);

  const horseNotifications = useMemo(() => {
    return (notifications ?? [])
      .filter((notification) => notification.horseExternalId === horse?.externalId)
      .sort((a, b) => {
        const aActive = activeNotificationIds.has(a.externalId);
        const bActive = activeNotificationIds.has(b.externalId);

        if (aActive === bActive) return 0;

        return aActive ? -1 : 1;
      });
  }, [notifications, horse?.externalId, activeNotificationIds]);

  const formatDate = (date) => {
    if (!date) return null;

    return new Date(date).toLocaleDateString(i18n.language === "fr" ? "fr-CA" : "en-CA");
  };

  const isSnoozed = (notification) => {
    if (!notification.snoozedUntil) return false;

    return new Date(notification.snoozedUntil) > new Date();
  };

  const handleAddNotification = () => {
    openNotificationModal();
  };

  const openNotificationModal = (notification = null) => {
    const isEdit = !!notification;

    openModal({
      title: isEdit ? i18n.t("NotificationModal.EditTitle") : i18n.t("NotificationModal.AddTitle"),

      form: "formNotification",

      hideSaveButton: false,
      hideSaveAndCloseButton: true,

      content: (
        <NotificationQuickForm
          ref={notificationFormRef}
          horse={horse}
          initialValue={notification}
        />
      ),

      canClose: async () => {
        const isDirty = notificationFormRef.current?.isDirty?.();

        if (!isDirty) return true;

        return window.confirm(i18n.t("Modal.UnsavedChangesConfirm"));
      },

      onSave: async () => {
        try {
          const isValid = notificationFormRef.current?.validate?.();

          if (!isValid) {
            return false;
          }

          const data = notificationFormRef.current.getFormData();

          let result;

          if (isEdit) {
            result = await NotificationService.UpdateNotification(data);
          } else {
            result = await NotificationService.AddNotification(data);
          }

          if (!result) {
            return false;
          }

          await loadNotifications();

          return true;
        } catch (ex) {
          console.error(ex);

          addToast(i18n.t("Generic.ServerError"), "BACKENDERROR", "ERROR", true);

          return false;
        }
      },

      onClose: () => {},
    });
  };

  const handleDelete = async (notification) => {
    if (!window.confirm(i18n.t("NotificationTab.DeleteConfirm"))) {
      return;
    }

    try {
      const result = await NotificationService.DeleteNotification(notification.externalId);

      if (result) {
        await loadNotifications();
      }
    } catch (ex) {
      addToast(i18n.t("Generic.ServerError"), "BACKENDERROR", "ERROR", true);
    }
  };

  const handleSnooze = async (notification, days) => {
    try {
      const result = await NotificationService.SnoozeNotification(notification.externalId, days);

      if (result) {
        await loadNotifications();
      }
    } catch (ex) {
      addToast(i18n.t("Generic.ServerError"), "BACKENDERROR", "ERROR", true);
    }
  };

  const handleClearSnooze = async (notification) => {
    try {
      const result = await NotificationService.ClearSnooze(notification.externalId);

      if (result) {
        await loadNotifications();
      }
    } catch (ex) {
      addToast(i18n.t("Generic.ServerError"), "BACKENDERROR", "ERROR", true);
    }
  };

  const renderNotification = (notification) => {
    const snoozed = isSnoozed(notification);
    const isActive = activeNotificationIds.has(notification.externalId);

    return (
      <div
        key={notification.externalId}
        className={`
        ${styles.notificationCard}
        ${snoozed ? styles.notificationCardSnoozed : ""}
        ${isActive ? styles.notificationCardActive : ""}
      `}>
        <div className={styles.cardHeader}>
          <div className={styles.notificationTitle}>
            {snoozed ? <MdOutlineNotificationsPaused /> : <MdNotificationsActive />}

            <span>{notification.userServiceName || i18n.t("NotificationTab.AllServices")}</span>

            {isActive && (
              <span className={styles.activeBadge}>
                <MdNotificationImportant />
                {"Active"}
              </span>
            )}
          </div>

          <button
            type="button"
            className={styles.deleteButton}
            onClick={() => handleDelete(notification)}
            title={i18n.t("Generic.Delete")}>
            <MdDeleteOutline />
          </button>
        </div>

        <div className={styles.message}>{notification.message}</div>

        <div className={styles.rule}>
          <MdOutlineAccessTime />

          <span>
            {i18n.t("NotificationTab.AfterDays", {
              days: notification.daysSinceLastService,
            })}
          </span>
        </div>

        <div className={styles.meta}>
          {notification.endOn && (
            <span>
              {i18n.t("NotificationTab.EndOn", {
                date: formatDate(notification.endOn),
              })}
            </span>
          )}

          {snoozed && (
            <span className={styles.snoozedLabel}>
              {i18n.t("NotificationTab.SnoozedUntil", {
                date: formatDate(notification.snoozedUntil),
              })}
            </span>
          )}
        </div>

        <div className={styles.actions}>
          {snoozed ? (
            <button
              type="button"
              className={styles.secondaryButton}
              onClick={() => handleClearSnooze(notification)}>
              {i18n.t("NotificationTab.CancelSnooze")}
            </button>
          ) : (
            <>
              <span className={styles.snoozeLabel}>{i18n.t("NotificationTab.Snooze")}</span>

              <button
                type="button"
                className={styles.snoozeButton}
                onClick={() => handleSnooze(notification, 1)}>
                1 {i18n.t("Generic.Day")}
              </button>

              <button
                type="button"
                className={styles.snoozeButton}
                onClick={() => handleSnooze(notification, 3)}>
                3 {i18n.t("Generic.Days")}
              </button>

              <button
                type="button"
                className={styles.snoozeButton}
                onClick={() => handleSnooze(notification, 7)}>
                7 {i18n.t("Generic.Days")}
              </button>
            </>
          )}
        </div>
      </div>
    );
  };

  const renderList = () => {
    if (isLoading) {
      return (
        <div className={styles.loading}>
          <LoadingSpinner />
        </div>
      );
    }

    if (!horseNotifications.length) {
      return <div className={styles.noItemFound}>{i18n.t("NotificationTab.NoNotification")}</div>;
    }

    return horseNotifications.map(renderNotification);
  };

  return (
    <div className={styles.notifications}>
      <div className={styles.sectionHeader}>
        <div className={styles.headerTitle}>{i18n.t("NotificationTab.Title")}</div>

        <button type="button" className={styles.headerButton} onClick={handleAddNotification}>
          {i18n.t("NotificationTab.Add")}
        </button>
      </div>

      <div className={styles.sectionItemsList}>{renderList()}</div>
    </div>
  );
};

export default HorseNotificationsTab;
